// Shared skeleton loader utility for POS product search
// Reuses the skeleton template from infinite scroll without modifying infinite_scroll_controller

class SkeletonLoader {
  constructor() {
    this.skeletonContainer = null
    this.isVisible = false
    this.activeDataAttribute = null
  }

  /**
   * Show skeleton loaders in the products container
   * @param {Object} options - Configuration options
   * @param {string} options.position - 'prepend' (top) or 'append' (bottom), default 'prepend'
   * @param {number} options.count - Number of skeleton cards to show, default 4
   * @param {boolean} options.clearContent - Whether to clear existing content, default true for prepend, false for append
   * @param {string} options.dataAttribute - Custom data attribute for identification, default 'data-skeleton-loader'
   */
  show(options = {}) {
    const { 
      position = 'prepend', 
      count = 4, 
      clearContent = position === 'prepend',
      dataAttribute = 'data-skeleton-loader'
    } = options

    // Don't create duplicates if already visible with the same data attribute
    if (this.isVisible && this.activeDataAttribute === dataAttribute) {
      return
    }
    
    // If a different type of loader is visible, hide it first
    if (this.isVisible && this.activeDataAttribute !== dataAttribute) {
      this.hide({ dataAttribute: this.activeDataAttribute })
    }

    // Look for the skeleton template in the DOM
    const skeletonTemplate = document.querySelector('#skeleton-card-template')
    if (!skeletonTemplate) {
      console.warn('Skeleton template not found')
      return
    }

    // Find the products wrapper - we'll create the container if it doesn't exist
    const productsWrapper = document.querySelector('.products-content-wrapper')
    if (!productsWrapper) {
      console.warn('Products wrapper not found')
      return
    }

    // Clear existing content only if clearContent is true (for form submissions)
    if (clearContent) {
      // Keep only the infinite scroll controller and skeleton template
      const elementsToKeep = productsWrapper.querySelectorAll('.infinite-scroll-controller, #skeleton-card-template')
      const keepSet = new Set(elementsToKeep)
      
      Array.from(productsWrapper.children).forEach(child => {
        if (!keepSet.has(child) && !child.classList.contains('infinite-scroll-controller')) {
          child.remove()
        }
      })
    }

    // Create container for skeleton loaders
    this.skeletonContainer = document.createElement('div')
    this.skeletonContainer.className = 'skeleton-loader-container space-y-3'
    this.skeletonContainer.setAttribute(dataAttribute, 'true')

    // Create skeleton cards using the template
    for (let i = 0; i < count; i++) {
      const skeletonCard = skeletonTemplate.content.cloneNode(true)
      this.skeletonContainer.appendChild(skeletonCard)
    }

    // Insert based on position
    if (position === 'prepend') {
      // For form submissions - add at top with padding
      this.skeletonContainer.classList.add('xl:px-0', 'px-4', 'bg-white')
      const infiniteScrollController = productsWrapper.querySelector('.infinite-scroll-controller')
      if (infiniteScrollController) {
        productsWrapper.insertBefore(this.skeletonContainer, infiniteScrollController)
      } else {
        productsWrapper.insertBefore(this.skeletonContainer, productsWrapper.firstChild)
      }
    } else {
      // For infinite scroll - append to existing products container
      const productsContainer = document.querySelector('.products-content-wrapper .space-y-3')
      if (productsContainer) {
        productsContainer.appendChild(this.skeletonContainer)
      } else {
        // Fallback if no products container exists yet
        const infiniteScrollController = productsWrapper.querySelector('.infinite-scroll-controller')
        if (infiniteScrollController) {
          productsWrapper.insertBefore(this.skeletonContainer, infiniteScrollController)
        }
      }
    }

    this.isVisible = true
    this.activeDataAttribute = dataAttribute
  }

  /**
   * Hide and remove skeleton loaders
   * @param {Object} options - Configuration options
   * @param {string} options.dataAttribute - Specific data attribute to remove, if not provided removes all
   */
  hide(options = {}) {
    const { dataAttribute } = options
    
    // Remove skeleton loader containers
    let selector = '[data-skeleton-loader="true"], [data-infinite-scroll-loader="true"]'
    if (dataAttribute) {
      selector = `[${dataAttribute}="true"]`
    }
    
    const containers = document.querySelectorAll(selector)
    containers.forEach(container => {
      if (container && container.parentNode) {
        container.parentNode.removeChild(container)
      }
    })

    // Only reset state if we're hiding the currently active loader
    if (!dataAttribute || dataAttribute === this.activeDataAttribute) {
      this.skeletonContainer = null
      this.isVisible = false
      this.activeDataAttribute = null
    }
  }

  /**
   * Check if skeleton loaders are currently visible
   */
  get visible() {
    return this.isVisible
  }
}

// Export singleton instance
export const skeletonLoader = new SkeletonLoader()

// Auto-hide form submission skeleton loaders when Turbo Frame loads results
// This ensures cleanup even if controllers forget to hide
// Note: Infinite scroll loaders are managed separately and not cleared by Turbo Frame events
document.addEventListener('turbo:frame-load', (event) => {
  const frame = event.target
  if (frame && frame.id === 'products-search-results') {
    // Only hide form submission loaders, not infinite scroll loaders
    skeletonLoader.hide({ dataAttribute: 'data-skeleton-loader' })
  }
})
